import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./MeteorSmash.css";
import { submitSoloGameResult } from "../../../services/games/gameService";

const WIDTH = 900;
const HEIGHT = 640;
const PLAYER_Y = HEIGHT - 74;
const HIGH_SCORE_KEY = "hobbli-meteor-smash-high-score-v1";
const SETTINGS_KEY = "hobbli-meteor-smash-settings-v1";

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const random = (min, max) => Math.random() * (max - min) + min;
const hitCircle = (a, b) => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const radius = (a.radius || 0) + (b.radius || 0);
  return dx * dx + dy * dy <= radius * radius;
};

function loadNumber(key, fallback = 0) {
  try {
    const value = Number(window.localStorage.getItem(key));
    return Number.isFinite(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

function loadSettings() {
  try {
    return JSON.parse(window.localStorage.getItem(SETTINGS_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function createStars() {
  return Array.from({ length: 115 }, () => ({
    x: Math.random() * WIDTH,
    y: Math.random() * HEIGHT,
    size: random(0.6, 2.2),
    alpha: random(0.25, 0.95),
    speed: random(4, 18),
  }));
}

function makeMeteor(wave, size = null, x = null, y = null) {
  const resolvedSize = size || (() => {
    const roll = Math.random();
    if (roll < 0.12) return "large";
    if (roll < 0.42) return "medium";
    return "small";
  })();

  const config = {
    large: { radius: random(29, 36), points: 10, penalty: 10, speedBonus: 0 },
    medium: { radius: random(20, 25), points: 20, penalty: 20, speedBonus: 16 },
    small: { radius: random(11, 16), points: 40, penalty: 40, speedBonus: 34 },
  }[resolvedSize];

  return {
    kind: "meteor",
    size: resolvedSize,
    x: x ?? random(config.radius + 12, WIDTH - config.radius - 12),
    y: y ?? -config.radius - random(0, 80),
    radius: config.radius,
    speed: random(58, 92) + wave * random(5.5, 8) + config.speedBonus,
    drift: random(-28, 28),
    spin: random(-2.8, 2.8),
    rotation: random(0, Math.PI * 2),
    points: config.points,
    groundPenalty: config.penalty,
  };
}

function splitMeteor(meteor, wave) {
  const nextSize = meteor.size === "large" ? "medium" : meteor.size === "medium" ? "small" : null;
  if (!nextSize) return [];

  return [-1, 1].map((direction) => {
    const fragment = makeMeteor(wave, nextSize, meteor.x, meteor.y);
    fragment.drift = direction * random(34, 62) + meteor.drift * 0.35;
    fragment.speed *= random(1.03, 1.14);
    fragment.x = clamp(meteor.x + direction * meteor.radius * 0.28, fragment.radius, WIDTH - fragment.radius);
    return fragment;
  });
}

function makeBomb(wave) {
  return {
    kind: "bomb",
    x: random(20, WIDTH - 20),
    y: -20,
    radius: 13,
    hp: 1,
    maxHp: 1,
    speed: 95 + wave * 9,
    drift: random(-10, 10),
    rotation: 0,
    spin: 0,
    points: 60,
  };
}

function makePowerUp() {
  const types = ["rapid", "spread", "shield"];
  return {
    kind: "powerup",
    power: types[Math.floor(Math.random() * types.length)],
    x: random(30, WIDTH - 30),
    y: -24,
    radius: 14,
    speed: 72,
    drift: 0,
    points: 0,
  };
}

function initialWorld() {
  return {
    player: { x: WIDTH / 2, y: PLAYER_Y, radius: 19, speed: 380 },
    bullets: [],
    enemies: [],
    particles: [],
    stars: createStars(),
    elapsed: 0,
    spawnTimer: 0,
    shotTimer: 0,
    invulnerable: 0,
    rapidTimer: 0,
    spreadTimer: 0,
    shield: 0,
  };
}

export default function MeteorSmash({ currentUser }) {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const frameRef = useRef(0);
  const lastRef = useRef(0);
  const worldRef = useRef(initialWorld());
  const keysRef = useRef({ left: false, right: false, fire: false });
  const pointerRef = useRef({ left: false, right: false, fire: false });
  const audioRef = useRef(null);
  const gameStartedAtRef = useRef(null);
  const resultSubmittedRef = useRef(false);

  const saved = typeof window !== "undefined" ? loadSettings() : {};
  const [status, setStatus] = useState("ready"); // ready | playing | paused | gameover
  const statusRef = useRef(status);
  const [score, setScore] = useState(0);
  const scoreRef = useRef(0);
  const [highScore, setHighScore] = useState(() =>
    typeof window !== "undefined" ? loadNumber(HIGH_SCORE_KEY, 0) : 0
  );
  const [lives, setLives] = useState(3);
  const livesRef = useRef(3);
  const [wave, setWave] = useState(1);
  const waveRef = useRef(1);
  const [muted, setMuted] = useState(saved.muted ?? false);
  const mutedRef = useRef(muted);
  const [autoFire, setAutoFire] = useState(saved.autoFire ?? false);
  const autoFireRef = useRef(autoFire);

  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { scoreRef.current = score; }, [score]);
  useEffect(() => { livesRef.current = lives; }, [lives]);
  useEffect(() => { waveRef.current = wave; }, [wave]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { autoFireRef.current = autoFire; }, [autoFire]);

  useEffect(() => {
    try {
      window.localStorage.setItem(SETTINGS_KEY, JSON.stringify({ muted, autoFire }));
    } catch {}
  }, [muted, autoFire]);

  const tone = useCallback((frequency = 440, duration = 0.04, volume = 0.035, type = "square") => {
    if (mutedRef.current) return;
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      if (!audioRef.current) audioRef.current = new AudioContext();
      const ctx = audioRef.current;
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = type;
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(volume, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + duration);
    } catch {}
  }, []);

  const addScore = useCallback((points) => {
    setScore((current) => {
      const next = Math.max(0, current + points);
      scoreRef.current = next;
      setHighScore((best) => {
        if (next <= best) return best;
        try { window.localStorage.setItem(HIGH_SCORE_KEY, String(next)); } catch {}
        return next;
      });
      return next;
    });
  }, []);

  const explode = useCallback((x, y, count = 12, force = 1) => {
    const particles = worldRef.current.particles;
    for (let i = 0; i < count; i += 1) {
      const angle = random(0, Math.PI * 2);
      const speed = random(45, 160) * force;
      particles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: random(0.28, 0.75),
        maxLife: 0.75,
        size: random(1.5, 4.8),
      });
    }
  }, []);

  const resetGame = useCallback(() => {
    worldRef.current = initialWorld();
    keysRef.current = { left: false, right: false, fire: false };
    pointerRef.current = { left: false, right: false, fire: false };
    scoreRef.current = 0;
    livesRef.current = 3;
    waveRef.current = 1;
    setScore(0);
    setLives(3);
    setWave(1);
    gameStartedAtRef.current = Date.now();
    resultSubmittedRef.current = false;
    setStatus("playing");
    tone(620, 0.08, 0.04, "sawtooth");
  }, [tone]);

  const endGame = useCallback(() => {
    setStatus("gameover");
    tone(100, 0.42, 0.055, "sawtooth");

    if (currentUser?.id && !resultSubmittedRef.current) {
      resultSubmittedRef.current = true;
      const durationMs = gameStartedAtRef.current
        ? Math.max(250, Date.now() - gameStartedAtRef.current)
        : null;

      submitSoloGameResult({
        gameKey: "meteor-smash",
        result: "completed",
        score: Math.max(0, Math.round(scoreRef.current || 0)),
        durationMs,
        stats: { wave: waveRef.current },
      }).catch((error) => {
        console.warn("Could not save Meteor Smash result to Hobbli:", error);
      });
    }
  }, [currentUser?.id, tone]);

  const loseLife = useCallback((source = "player") => {
    const world = worldRef.current;
    const surfaceHit = source === "surface";

    // Temporary player invulnerability and shields protect the cannon itself,
    // but a bomb that reaches the surface always costs a life.
    if (!surfaceHit && world.invulnerable > 0) return;
    if (!surfaceHit && world.shield > 0) {
      world.shield = 0;
      world.invulnerable = 0.8;
      explode(world.player.x, world.player.y, 18, 0.65);
      tone(220, 0.12, 0.05, "triangle");
      return;
    }

    explode(
      surfaceHit ? WIDTH / 2 : world.player.x,
      surfaceHit ? HEIGHT - 18 : world.player.y,
      surfaceHit ? 20 : 28,
      surfaceHit ? 0.8 : 1.15
    );

    const next = livesRef.current - 1;
    livesRef.current = next;
    setLives(next);

    if (!surfaceHit) {
      world.invulnerable = 1.7;
      world.player.x = WIDTH / 2;
    }

    tone(125, 0.2, 0.06, "sawtooth");
    if (next <= 0) endGame();
  }, [endGame, explode, tone]);

  const shoot = useCallback(() => {
    const world = worldRef.current;
    if (world.shotTimer > 0) return;
    const cooldown = world.rapidTimer > 0 ? 0.095 : 0.19;
    world.shotTimer = cooldown;
    const spread = world.spreadTimer > 0 ? [-0.22, 0, 0.22] : [0];
    spread.forEach((angle) => {
      const speed = 560;
      world.bullets.push({
        x: world.player.x,
        y: world.player.y - 24,
        vx: Math.sin(angle) * speed,
        vy: -Math.cos(angle) * speed,
        radius: 4,
      });
    });
    tone(world.rapidTimer > 0 ? 760 : 640, 0.035, 0.025, "square");
  }, [tone]);

  const update = useCallback((dt) => {
    if (statusRef.current !== "playing") return;
    const world = worldRef.current;
    const waveNow = waveRef.current;
    world.elapsed += dt;
    world.spawnTimer -= dt;
    world.shotTimer = Math.max(0, world.shotTimer - dt);
    world.invulnerable = Math.max(0, world.invulnerable - dt);
    world.rapidTimer = Math.max(0, world.rapidTimer - dt);
    world.spreadTimer = Math.max(0, world.spreadTimer - dt);

    const left = keysRef.current.left || pointerRef.current.left;
    const right = keysRef.current.right || pointerRef.current.right;
    if (left !== right) {
      world.player.x += (right ? 1 : -1) * world.player.speed * dt;
      world.player.x = clamp(world.player.x, 28, WIDTH - 28);
    }
    if (keysRef.current.fire || pointerRef.current.fire || autoFireRef.current) shoot();

    world.stars.forEach((star) => {
      star.y += star.speed * dt;
      if (star.y > HEIGHT) { star.y = 0; star.x = Math.random() * WIDTH; }
    });

    if (world.spawnTimer <= 0) {
      const roll = Math.random();
      const powerChance = Math.min(0.035 + waveNow * 0.002, 0.06);
      if (roll < powerChance) world.enemies.push(makePowerUp());
      else if (roll < powerChance + Math.min(0.08 + waveNow * 0.008, 0.18)) world.enemies.push(makeBomb(waveNow));
      else world.enemies.push(makeMeteor(waveNow));
      world.spawnTimer = Math.max(0.25, 0.92 - waveNow * 0.055) * random(0.72, 1.22);
    }

    world.bullets.forEach((bullet) => {
      bullet.x += bullet.vx * dt;
      bullet.y += bullet.vy * dt;
    });
    world.bullets = world.bullets.filter((bullet) => bullet.y > -30 && bullet.x > -30 && bullet.x < WIDTH + 30);

    world.enemies.forEach((enemy) => {
      enemy.x += (enemy.drift || 0) * dt;
      enemy.y += enemy.speed * dt;
      enemy.rotation = (enemy.rotation || 0) + (enemy.spin || 0) * dt;
      enemy.x = clamp(enemy.x, enemy.radius, WIDTH - enemy.radius);
    });

    const consumedBullets = new Set();
    const consumedEnemies = new Set();
    world.enemies.forEach((enemy, ei) => {
      if (enemy.kind === "powerup") return;
      world.bullets.forEach((bullet, bi) => {
        if (consumedBullets.has(bi) || consumedEnemies.has(ei)) return;
        if (!hitCircle(enemy, bullet)) return;
        consumedBullets.add(bi);
        consumedEnemies.add(ei);
        explode(bullet.x, bullet.y, 5, 0.35);
        addScore(enemy.points + Math.max(0, waveNow - 1) * 2);
        explode(enemy.x, enemy.y, enemy.kind === "bomb" ? 24 : 16, enemy.kind === "bomb" ? 1.25 : 0.75);
        tone(enemy.kind === "bomb" ? 150 : 240, enemy.kind === "bomb" ? 0.12 : 0.065, 0.035, "triangle");

        if (enemy.kind === "meteor") {
          world.enemies.push(...splitMeteor(enemy, waveNow));
        }
      });
    });
    world.bullets = world.bullets.filter((_, index) => !consumedBullets.has(index));
    world.enemies = world.enemies.filter((_, index) => !consumedEnemies.has(index));

    const survivors = [];
    world.enemies.forEach((enemy) => {
      if (enemy.kind === "powerup" && hitCircle(enemy, world.player)) {
        if (enemy.power === "rapid") world.rapidTimer = 9;
        if (enemy.power === "spread") world.spreadTimer = 9;
        if (enemy.power === "shield") world.shield = 1;
        addScore(40);
        tone(980, 0.12, 0.035, "sine");
        return;
      }
      if (enemy.kind !== "powerup" && hitCircle(enemy, world.player)) {
        loseLife();
        return;
      }
      if (enemy.y - enemy.radius > HEIGHT) {
        if (enemy.kind === "bomb") {
          loseLife("surface");
        } else if (enemy.kind === "meteor") {
          addScore(-enemy.groundPenalty);
          tone(95, 0.08, 0.025, "triangle");
        }
        return;
      }
      survivors.push(enemy);
    });
    world.enemies = survivors;

    world.particles.forEach((particle) => {
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      particle.vx *= 0.985;
      particle.vy *= 0.985;
      particle.life -= dt;
    });
    world.particles = world.particles.filter((p) => p.life > 0);

    const targetWave = Math.floor(world.elapsed / 24) + 1;
    if (targetWave !== waveRef.current) {
      waveRef.current = targetWave;
      setWave(targetWave);
      addScore(100 * targetWave);
      tone(840, 0.14, 0.04, "triangle");
    }
  }, [addScore, explode, loseLife, shoot, tone]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const world = worldRef.current;

    const gradient = ctx.createLinearGradient(0, 0, 0, HEIGHT);
    gradient.addColorStop(0, "#030815");
    gradient.addColorStop(0.62, "#07142b");
    gradient.addColorStop(1, "#0d2343");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    world.stars.forEach((star) => {
      ctx.globalAlpha = star.alpha;
      ctx.fillStyle = "#d9f7ff";
      ctx.fillRect(star.x, star.y, star.size, star.size);
    });
    ctx.globalAlpha = 1;

    const ground = ctx.createLinearGradient(0, HEIGHT - 38, 0, HEIGHT);
    ground.addColorStop(0, "#17395d");
    ground.addColorStop(1, "#06101d");
    ctx.fillStyle = ground;
    ctx.fillRect(0, HEIGHT - 32, WIDTH, 32);
    for (let x = 0; x < WIDTH; x += 36) {
      ctx.fillStyle = x % 72 === 0 ? "#2f638c" : "#234967";
      ctx.fillRect(x, HEIGHT - 30 + (x % 3), 28, 7);
    }

    world.bullets.forEach((bullet) => {
      ctx.save();
      ctx.shadowBlur = 16;
      ctx.shadowColor = "#42e9ff";
      ctx.strokeStyle = "#e8feff";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(bullet.x, bullet.y + 9);
      ctx.lineTo(bullet.x, bullet.y - 9);
      ctx.stroke();
      ctx.restore();
    });

    world.enemies.forEach((enemy) => {
      ctx.save();
      ctx.translate(enemy.x, enemy.y);
      ctx.rotate(enemy.rotation || 0);
      if (enemy.kind === "meteor") {
        ctx.shadowBlur = 8;
        ctx.shadowColor = "rgba(255,140,50,.35)";
        const rock = ctx.createRadialGradient(-enemy.radius * 0.3, -enemy.radius * 0.35, 2, 0, 0, enemy.radius);
        rock.addColorStop(0, "#aeb7c2");
        rock.addColorStop(0.45, "#687587");
        rock.addColorStop(1, "#2f3948");
        ctx.fillStyle = rock;
        ctx.beginPath();
        const points = 10;
        for (let i = 0; i < points; i += 1) {
          const angle = (i / points) * Math.PI * 2;
          const r = enemy.radius * (0.82 + ((i * 37) % 17) / 100);
          const px = Math.cos(angle) * r;
          const py = Math.sin(angle) * r;
          if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "rgba(20,28,40,.35)";
        ctx.beginPath(); ctx.arc(-enemy.radius * 0.25, -enemy.radius * 0.18, enemy.radius * 0.2, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(enemy.radius * 0.32, enemy.radius * 0.2, enemy.radius * 0.14, 0, Math.PI * 2); ctx.fill();
      } else if (enemy.kind === "bomb") {
        ctx.shadowBlur = 18;
        ctx.shadowColor = "#ff5b2e";
        ctx.fillStyle = "#161b23";
        ctx.beginPath(); ctx.arc(0, 2, 11, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = "#ffcb4a"; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(4, -8); ctx.quadraticCurveTo(10, -16, 14, -12); ctx.stroke();
        ctx.fillStyle = "#ff5b2e"; ctx.beginPath(); ctx.arc(14, -12, 3.5, 0, Math.PI * 2); ctx.fill();
      } else {
        const colors = enemy.power === "shield" ? ["#69d7ff", "#dffaff"] : enemy.power === "spread" ? ["#d66dff", "#ffe1ff"] : ["#ffd45b", "#fff1b0"];
        ctx.shadowBlur = 20; ctx.shadowColor = colors[0];
        ctx.strokeStyle = colors[0]; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.arc(0, 0, 13, 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = colors[1];
        ctx.font = "bold 14px sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText(enemy.power === "shield" ? "S" : enemy.power === "spread" ? "3" : "R", 0, 1);
      }
      ctx.restore();
    });

    world.particles.forEach((particle) => {
      ctx.save();
      ctx.globalAlpha = clamp(particle.life / particle.maxLife, 0, 1);
      ctx.fillStyle = particle.life > 0.35 ? "#ffdd7a" : "#ff6438";
      ctx.shadowBlur = 9;
      ctx.shadowColor = "#ff7a32";
      ctx.beginPath(); ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    });

    const p = world.player;
    ctx.save();
    ctx.translate(p.x, p.y);
    if (world.invulnerable > 0 && Math.floor(world.invulnerable * 12) % 2 === 0) ctx.globalAlpha = 0.25;
    if (world.shield > 0) {
      ctx.strokeStyle = "#63e4ff"; ctx.lineWidth = 3; ctx.shadowBlur = 20; ctx.shadowColor = "#63e4ff";
      ctx.beginPath(); ctx.arc(0, 0, 30, 0, Math.PI * 2); ctx.stroke();
    }
    ctx.shadowBlur = 14; ctx.shadowColor = "#e9f8ff";
    ctx.fillStyle = "#e7eef7";
    ctx.beginPath(); ctx.moveTo(0, -27); ctx.lineTo(-20, 20); ctx.lineTo(-6, 14); ctx.lineTo(0, 21); ctx.lineTo(6, 14); ctx.lineTo(20, 20); ctx.closePath(); ctx.fill();
    ctx.fillStyle = "#ec493d";
    ctx.beginPath(); ctx.moveTo(-17, 12); ctx.lineTo(-28, 24); ctx.lineTo(-10, 20); ctx.closePath(); ctx.fill();
    ctx.beginPath(); ctx.moveTo(17, 12); ctx.lineTo(28, 24); ctx.lineTo(10, 20); ctx.closePath(); ctx.fill();
    ctx.fillStyle = "#27d9ff";
    ctx.beginPath(); ctx.ellipse(0, -8, 6, 10, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#ffb12f";
    ctx.beginPath(); ctx.moveTo(-5, 20); ctx.lineTo(0, 36 + Math.random() * 7); ctx.lineTo(5, 20); ctx.closePath(); ctx.fill();
    ctx.restore();

    if (statusRef.current === "paused") {
      ctx.fillStyle = "rgba(0,0,0,.5)"; ctx.fillRect(0, 0, WIDTH, HEIGHT);
      ctx.fillStyle = "#eafaff"; ctx.font = "800 42px system-ui"; ctx.textAlign = "center"; ctx.fillText("PAUSED", WIDTH / 2, HEIGHT / 2);
    }
  }, []);

  useEffect(() => {
    const loop = (time) => {
      const previous = lastRef.current || time;
      const dt = Math.min((time - previous) / 1000, 0.034);
      lastRef.current = time;
      update(dt);
      draw();
      frameRef.current = requestAnimationFrame(loop);
    };
    frameRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frameRef.current);
  }, [draw, update]);

  useEffect(() => {
    const setKey = (event, down) => {
      const key = event.key.toLowerCase();
      if (["arrowleft", "arrowright", " ", "a", "d", "p", "escape"].includes(key)) event.preventDefault();
      if (key === "arrowleft" || key === "a") keysRef.current.left = down;
      if (key === "arrowright" || key === "d") keysRef.current.right = down;
      if (key === " ") keysRef.current.fire = down;
      if (down && (key === "p" || key === "escape")) {
        setStatus((current) => current === "playing" ? "paused" : current === "paused" ? "playing" : current);
      }
      if (down && key === "enter" && (statusRef.current === "ready" || statusRef.current === "gameover")) resetGame();
    };
    const down = (event) => setKey(event, true);
    const up = (event) => setKey(event, false);
    window.addEventListener("keydown", down, { passive: false });
    window.addEventListener("keyup", up, { passive: false });
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [resetGame]);

  const bindHold = (name) => ({
    onPointerDown: (event) => {
      event.preventDefault();
      pointerRef.current[name] = true;
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    onPointerUp: (event) => { event.preventDefault(); pointerRef.current[name] = false; },
    onPointerCancel: () => { pointerRef.current[name] = false; },
    onPointerLeave: () => { pointerRef.current[name] = false; },
  });

  const powerText = [
    worldRef.current.shield ? "SHIELD" : null,
    worldRef.current.rapidTimer > 0 ? `RAPID ${Math.ceil(worldRef.current.rapidTimer)}s` : null,
    worldRef.current.spreadTimer > 0 ? `SPREAD ${Math.ceil(worldRef.current.spreadTimer)}s` : null,
  ].filter(Boolean).join(" · ");

  return (
    <div className="meteor-smash-page">
      <section className="meteor-smash-shell">
        <header className="meteor-smash-header">
          <button type="button" className="meteor-back" onClick={() => navigate("/games")}>← Game Room</button>
          <div className="meteor-title">
            <span>Hobbli Game Room</span>
            <h1>Meteor Smash</h1>
            <p>Defend · Shoot · Survive</p>
          </div>
          <button type="button" className="meteor-sound" onClick={() => setMuted((value) => !value)} aria-label={muted ? "Turn sound on" : "Mute sound"}>
            {muted ? "🔇 Sound Off" : "🔊 Sound On"}
          </button>
        </header>

        <div className="meteor-layout">
          <main className="meteor-arcade">
            <div className="meteor-scorebar">
              <div><span>Score</span><strong>{String(score).padStart(6, "0")}</strong></div>
              <div><span>High</span><strong>{String(highScore).padStart(6, "0")}</strong></div>
              <div><span>Lives</span><strong>{"♥".repeat(Math.max(0, lives)) || "—"}</strong></div>
              <div><span>Wave</span><strong>{wave}</strong></div>
            </div>

            <div className="meteor-canvas-wrap">
              <canvas ref={canvasRef} width={WIDTH} height={HEIGHT} className="meteor-canvas" aria-label="Meteor Smash game field" />

              {status === "ready" && (
                <div className="meteor-overlay">
                  <div className="meteor-overlay-card">
                    <span className="meteor-kicker">Incoming!</span>
                    <h2>Protect the surface.</h2>
                    <p>Move along the bottom, shatter meteors into smaller fragments, stop the bombs, collect power-ups, and survive increasingly fast waves.</p>
                    <button type="button" onClick={resetGame}>Start Game</button>
                  </div>
                </div>
              )}

              {status === "gameover" && (
                <div className="meteor-overlay">
                  <div className="meteor-overlay-card">
                    <span className="meteor-kicker">Game Over</span>
                    <h2>{score >= highScore && score > 0 ? "New high score!" : "The meteors got through."}</h2>
                    <p>Final score: <strong>{score.toLocaleString()}</strong> · Wave {wave}</p>
                    <button type="button" onClick={resetGame}>Play Again</button>
                  </div>
                </div>
              )}
            </div>

            <div className="meteor-mobile-controls" aria-label="Touch controls">
              <div className="meteor-fire-cluster">
                <button type="button" className="meteor-fire" {...bindHold("fire")} aria-label="Fire">FIRE</button>
                <button
                  type="button"
                  className={`meteor-auto-fire ${autoFire ? "is-active" : ""}`}
                  onClick={() => setAutoFire((value) => !value)}
                  aria-pressed={autoFire}
                >
                  AUTO {autoFire ? "ON" : "OFF"}
                </button>
              </div>
              <div className="meteor-move-cluster">
                <button type="button" {...bindHold("left")} aria-label="Move left">◀</button>
                <button type="button" {...bindHold("right")} aria-label="Move right">▶</button>
              </div>
            </div>

            <div className="meteor-underbar">
              <span>{powerText || "Destroy anything that reaches the surface."}</span>
              <div className="meteor-underbar-actions">
                <button
                  type="button"
                  className={autoFire ? "is-active" : ""}
                  onClick={() => setAutoFire((value) => !value)}
                  aria-pressed={autoFire}
                >
                  Auto Fire {autoFire ? "On" : "Off"}
                </button>
                {(status === "playing" || status === "paused") && (
                  <button type="button" onClick={() => setStatus((current) => current === "playing" ? "paused" : "playing")}>{status === "paused" ? "Resume" : "Pause"}</button>
                )}
              </div>
            </div>
          </main>

          <aside className="meteor-sidebar">
            <section>
              <h2>Controls</h2>
              <p><b>← / A</b><span>Move left</span></p>
              <p><b>→ / D</b><span>Move right</span></p>
              <p><b>Space</b><span>Fire</span></p>
              <p><b>P / Esc</b><span>Pause</span></p>
            </section>
            <section>
              <h2>Power-Ups</h2>
              <p><b>R</b><span>Rapid fire</span></p>
              <p><b>3</b><span>Triple spread</span></p>
              <p><b>S</b><span>Shield</span></p>
            </section>
            <section className="meteor-tip">
              <h2>How scoring works</h2>
              <p className="meteor-copy">Every meteor is destroyed in one shot. Large rocks split into medium fragments, medium rocks split into small fragments, and smaller targets are worth more. Missed meteors cost points; bombs reaching the ground cost a life.</p>
            </section>
          </aside>
        </div>
      </section>
    </div>
  );
}
